<template>
  <Modal
    v-if="isVisible"
    :show="isVisible"
    :title="t('Editar configuração')"
    :save-label="t('Salvar')"
    :close-label="t('Fechar')"
    :save-fn="handleSave"
    elem-id="modalConfiguracao"
    @close="close"
    @dismiss="close"
    >
      

    <inline-message v-if="isLoading" severity="info" :closable="false">
      {{ t('Carregando') }}...
    </inline-message>

    <div v-else>
      <div class="form-group">
        <label>{{ t('Nome') }}</label>
        <p class="form-control-static">{{ configData.nome }}</p>
      </div>

      <div class="form-group">
        <label>{{ t('Descrição') }}</label>
        <p class="form-control-static">{{ configData.descricao }}</p>
      </div>

      <div class="form-group">
        <label>{{ t('Valor padrão') }}</label>
        <p class="form-control-static">{{ configData.valor_padrao }}</p>
      </div>

      <input-text
        data-testid="franquias-configuration-modal-valor"
        elem-id="inputConfiguracao"
        :label="t('Valor')"
        :placeholder="t('Digite o valor da configuração')"
        v-model="formData.valor"
        :required="false" />
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { useConfiguracoesSistemaFranquia } from '../../composables/useConfiguracoesSistemaFranquia';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface ConfigData {
  nome: string;
  descricao: string;
  valor_padrao: string;
  valor: string;
  [key: string]: unknown;
}

interface FormData {
  valor: string;
}

interface ModalOpenPayload {
  franquiaId?: Id;
  configNome?: string;
}

interface Props {
  franquiaId?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaId: null,
});

const { t } = useI18n();
const { fetchConfigsWithFranquiaData, saveConfig } = useConfiguracoesSistemaFranquia();

const emit = defineEmits<{
  'config-saved': [];
}>();

// Reactive data
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const currentFranquiaId = ref<Id | null>(null);
const configData = ref<ConfigData>({
  nome: '',
  descricao: '',
  valor_padrao: '',
  valor: '',
});
const formData = ref<FormData>({
  valor: '',
});

// Methods
const close = (): void => {
  log('ConfigurationModal', 'close', 'Closing modal');
  isVisible.value = false;
  configData.value = {
    nome: '',
    descricao: '',
    valor_padrao: '',
    valor: '',
  };
  formData.value.valor = '';
};

const open = async (configNome: string): Promise<void> => {
  log('ConfigurationModal', 'open', 'Opening modal for config', configNome);
  isVisible.value = true;
  isLoading.value = true;

  // Use internal state or fallback to prop
  const franquiaId = currentFranquiaId.value || props.franquiaId;
  if (!franquiaId) {
    log('ConfigurationModal', 'open', 'No franquiaId available');
    eventBus.emit('globalMessage.error', t('Franquia não especificada'));
    close();
    return;
  }

  try {
    // Fetch all configurations to find the one we need
    const configs = (await fetchConfigsWithFranquiaData(Number(franquiaId))) as ConfigData[];
    const config = configs.find((c) => c.nome === configNome);

    if (config) {
      configData.value = { ...config };
      formData.value.valor = (config.valor as string) || (config.valor_padrao as string) || '';
    } else {
      log('ConfigurationModal', 'open', 'Configuration not found', configNome);
      eventBus.emit('globalMessage.error', t('Configuração não encontrada'));
      close();
    }
  } catch (error) {
    log('ConfigurationModal', 'open', 'Error loading configuration', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar configuração'));
    close();
  } finally {
    isLoading.value = false;
  }
};

// Event handlers for centralized ModalManager
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('ConfigurationModal', 'handleModalOpen', 'Modal open event received', data);
  if (data?.franquiaId) {
    currentFranquiaId.value = data.franquiaId;
  }
  if (data?.configNome) {
    void open(data.configNome);
  }
};

const handleSave = async (): Promise<void> => {
  log('ConfigurationModal', 'handleSave', 'Saving configuration');

  const franquiaId = currentFranquiaId.value || props.franquiaId;
  try {
    const saveData = {
      nome: configData.value.nome,
      franquia_id: Number(franquiaId),
      valor: formData.value.valor,
    };

    await saveConfig(saveData);

    eventBus.emit('globalMessage.success', t('Configuração salva com sucesso'));

    emit('config-saved');
    close();
  } catch (error) {
    log('ConfigurationModal', 'handleSave', 'Error saving configuration', error);
    eventBus.emit('globalMessage.error', t('Erro ao salvar configuração'));
  }
};

// Lifecycle hooks for centralized ModalManager
onMounted(() => {
  log('ConfigurationModal', 'onMounted', 'Component mounted');
  eventBus.on('modal.configuration.open', handleModalOpen);
  eventBus.on('modal.configuration.close', close);
});

onBeforeUnmount(() => {
  log('ConfigurationModal', 'onBeforeUnmount', 'Component unmounting');
  eventBus.off('modal.configuration.open', handleModalOpen);
  eventBus.off('modal.configuration.close', close);
});

// Expose methods for parent
defineExpose({
  open,
  close,
});
</script>

<style scoped>
.form-control-static {
  padding: 7px 0;
  margin-bottom: 0;
  min-height: 34px;
}

/* PrimeVue Message handles styling */
</style>
